import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import { ArrowLeft, ArrowRight, CheckCircle2, Loader2, Sparkles } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/re/portal/resident/baseline`;
const err = (e) => { const d = e?.response?.data?.detail; return typeof d === "string" ? d : "Something went wrong. Try again."; };
const gold = "bg-[#d4a017] text-black";

function Scale({ label, value, min = 1, onPick, testid }) {
  return (
    <div className="space-y-2">
      <div className="text-sm text-white/70">{label}</div>
      <div className="grid grid-cols-10 gap-1" role="radiogroup" aria-label={label}>
        {Array.from({ length: 10 }, (_, i) => i + 1).map((n) => (
          <button key={n} type="button" role="radio" aria-checked={value === n} disabled={n < min} onClick={() => onPick(n)} data-testid={`${testid}-${n}`}
            className={`h-11 rounded-md text-sm font-semibold transition-colors disabled:opacity-20 ${value === n ? gold : "bg-white/[0.06] text-white/80 hover:bg-white/10"}`}>{n}</button>
        ))}
      </div>
      <div className="flex justify-between text-[11px] text-white/40"><span>1 · not at all</span><span>10 · fully there</span></div>
    </div>
  );
}

function Question({ q, a, set, minWhy, i }) {
  const len = (a.narrative || "").trim().length;
  return (
    <div className="space-y-6" data-testid={`baseline-q-${q.question_no}`}>
      <div className="space-y-2">
        <div className="text-[11px] uppercase tracking-[0.25em] text-[#d4a017]">{q.pillar_label} · {i + 1} of 30</div>
        <h2 className="text-2xl sm:text-3xl font-semibold" data-testid="baseline-q-title">{q.title}</h2>
        <p className="text-white/70">{q.prompt}</p>
      </div>
      <Scale label="Where are you right now?" value={a.current} onPick={(n) => set({ current: n, target: a.target && a.target < n ? n : a.target })} testid="baseline-now" />
      <Scale label="Where do you want to be?" value={a.target} min={a.current || 1} onPick={(n) => set({ target: n })} testid="baseline-target" />
      <label className="block space-y-2"><span className="text-sm text-white/70">Why? Tell us in your own words.</span>
        <textarea rows={4} value={a.narrative || ""} onChange={(e) => set({ narrative: e.target.value })} data-testid="baseline-why"
          className="w-full rounded-md bg-black/40 border border-white/15 px-3 py-2.5 text-white outline-none focus:border-[#d4a017]" placeholder="What does this look like for you today? What would 'there' look like?" />
        <span className={`text-[11px] ${len >= minWhy ? "text-emerald-300" : "text-white/40"}`}>{len >= minWhy ? "Good" : `${minWhy - len} more characters`}</span></label>
    </div>
  );
}

const done = (a, minWhy) => a && a.current && a.target && (a.narrative || "").trim().length >= minWhy;

/** The 30-question baseline that opens a resident's journey. Shown instead of the portal until it's finished. */
export function BaselineGate({ token, onDone }) {
  const [d, setD] = useState(null);
  const [ans, setAns] = useState({});
  const [step, setStep] = useState(-1);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [record, setRecord] = useState(null);
  const [fromReview, setFromReview] = useState(false);
  const timer = useRef();
  useEffect(() => {
    axios.get(API, { params: { token } }).then((r) => {
      setD(r.data);
      const m = Object.fromEntries((r.data.draft || []).map((a) => [a.question_no, a]));
      setAns(m);
      const first = r.data.questions.findIndex((q) => !done(m[q.question_no], r.data.min_why));
      if (Object.keys(m).length) setStep(first === -1 ? 30 : first);
    }).catch((e) => setError(err(e)));
  }, [token]);
  const save = (next) => {
    clearTimeout(timer.current);
    timer.current = setTimeout(() => axios.put(`${API}/draft`, { answers: Object.values(next) }, { params: { token } }).catch(() => {}), 1200);
  };
  if (error && !d) return <p className="text-red-300" data-testid="baseline-error">{error}</p>;
  if (!d) return <div className="flex items-center gap-2 text-white/60"><Loader2 className="h-4 w-4 animate-spin" /> Loading…</div>;
  const qs = d.questions;
  const count = qs.filter((q) => done(ans[q.question_no], d.min_why)).length;
  const submit = async () => {
    setBusy(true); setError("");
    clearTimeout(timer.current);
    try { const r = await axios.post(API, { answers: Object.values(ans) }, { params: { token } }); setRecord(r.data.record); } catch (e) { setError(err(e)); } finally { setBusy(false); }
  };
  if (record) return (
    <div className="space-y-6" data-testid="baseline-done">
      <CheckCircle2 className="h-10 w-10 text-emerald-300" />
      <h1 className="text-4xl font-semibold">Your starting line is set, {d.first_name}.</h1>
      <p className="text-white/70">This is where your journey begins. Your team will use it to build your roadmap with you.</p>
      <div className="grid sm:grid-cols-3 gap-3">{Object.entries(record.pillar_summary).map(([k, s]) => (
        <div key={k} data-testid={`baseline-summary-${k}`} className="rounded-md border border-white/10 bg-white/[0.03] p-4"><div className="text-xs text-[#d4a017]">{s.label}</div>
          <div className="text-2xl font-semibold mt-1">{s.avg_current} → {s.avg_target}</div><div className="text-xs text-white/50">average now → goal</div></div>))}</div>
      <button type="button" onClick={onDone} data-testid="baseline-enter-portal" className={`inline-flex items-center gap-2 rounded-md px-5 py-3 font-medium ${gold}`}>Enter my portal <ArrowRight className="h-4 w-4" /></button>
    </div>
  );
  if (step === -1) return (
    <div className="space-y-6" data-testid="baseline-intro">
      <Sparkles className="h-8 w-8 text-[#d4a017]" />
      <h1 className="text-4xl sm:text-5xl font-semibold">Welcome, {d.first_name}.</h1>
      <p className="text-white/70">Before your portal opens, let's set your starting line. 30 short questions across three parts of your life. For each one: where you are now, where you want to be, and why. There are no wrong answers. Be honest — this is for you.</p>
      <div className="grid sm:grid-cols-3 gap-3">{Object.values(d.pillars).map((p) => <div key={p} className="rounded-md border border-white/10 bg-white/[0.03] p-4 text-sm">{p}<div className="text-xs text-white/50 mt-1">10 questions</div></div>)}</div>
      <p className="text-xs text-white/50">About 20 minutes. Your answers save as you go, so you can stop and come back.</p>
      <button type="button" onClick={() => setStep(0)} data-testid="baseline-start" className={`inline-flex items-center gap-2 rounded-md px-5 py-3 font-medium ${gold}`}>Let's begin <ArrowRight className="h-4 w-4" /></button>
    </div>
  );
  const bar = <div className="h-1.5 rounded-full bg-white/10 overflow-hidden" data-testid="baseline-progress"><div className="h-full bg-[#d4a017] transition-all" style={{ width: `${(count / 30) * 100}%` }} /></div>;
  if (step >= 30) return (
    <div className="space-y-5" data-testid="baseline-review">
      {bar}
      <h2 className="text-2xl font-semibold">Look it over</h2>
      <p className="text-sm text-white/60">{count} of 30 finished. Tap any question to change it.</p>
      <ul className="divide-y divide-white/5 rounded-md border border-white/10">{qs.map((q, i) => { const a = ans[q.question_no] || {}; const ok = done(a, d.min_why);
        return <li key={q.question_no}><button type="button" onClick={() => { setFromReview(true); setStep(i); }} data-testid={`baseline-review-${q.question_no}`} className="w-full text-left px-4 py-2.5 flex items-center gap-3 hover:bg-white/[0.03]">
          <span className="text-white/40 w-6 text-xs">{q.question_no}</span><span className="flex-1 text-sm">{q.title}</span>
          <span className={`text-xs ${ok ? "text-white/70" : "text-amber-300"}`}>{ok ? `${a.current} → ${a.target}` : "Not finished"}</span></button></li>; })}</ul>
      {error && <p className="text-sm text-red-300" data-testid="baseline-submit-error">{error}</p>}
      <button type="button" disabled={busy || count < 30} onClick={submit} data-testid="baseline-submit" className={`inline-flex items-center gap-2 rounded-md px-5 py-3 font-medium disabled:opacity-40 ${gold}`}>
        {busy && <Loader2 className="h-4 w-4 animate-spin" />} Set my starting line</button>
    </div>
  );
  const q = qs[step];
  const a = ans[q.question_no] || { question_no: q.question_no };
  const set = (u) => { const next = { ...ans, [q.question_no]: { ...a, ...u } }; setAns(next); save(next); };
  return (
    <div className="space-y-6">
      {bar}
      <Question q={q} a={a} set={set} minWhy={d.min_why} i={step} />
      <div className="flex items-center gap-3">
        <button type="button" onClick={() => setStep(step - 1)} data-testid="baseline-back" className="inline-flex items-center gap-1 text-sm text-white/60 hover:text-white"><ArrowLeft className="h-4 w-4" /> Back</button>
        <button type="button" disabled={!done(a, d.min_why)} onClick={() => { if (fromReview) { setFromReview(false); setStep(30); } else setStep(step + 1); }} data-testid="baseline-next" className={`ml-auto inline-flex items-center gap-2 rounded-md px-5 py-3 font-medium disabled:opacity-40 ${gold}`}>
          {fromReview ? "Back to review" : step === 29 ? "Review" : "Next"} <ArrowRight className="h-4 w-4" /></button>
      </div>
    </div>
  );
}
